import type { ProgressState, SSEEventData } from "@/types";
import type { DraftStreamCompletion } from "@/types/drafting";
import { apiUrl } from "@/lib/api-config";

export function parseDraftCompletePayload(data: SSEEventData): DraftStreamCompletion | null {
  const payload = data as SSEEventData & DraftStreamCompletion;
  if (payload.matterId || payload.versionId || payload.result?.documentModel) {
    return {
      matterId: payload.matterId,
      versionId: payload.versionId,
      status: payload.status,
      result: payload.result,
    };
  }

  if (data.status === "success" || data.status === "completed" || !data.status) {
    const result = (data.result ?? data) as DraftStreamCompletion;
    if (result?.result?.documentModel || result?.versionId) {
      return result;
    }
  }

  return null;
}

export function applyDraftSseProgressEvent(prev: ProgressState, data: SSEEventData): ProgressState {
  if (data.message) {
    return {
      ...prev,
      message: data.message,
      step: data.step,
      progress: data.progress !== undefined ? data.progress : prev.progress,
      isConnected: true,
      logs: [...prev.logs, { timestamp: new Date(), message: data.message, step: data.step }],
    };
  }
  if (data.progress !== undefined) {
    return { ...prev, progress: data.progress, isConnected: true };
  }
  return prev;
}

export interface DraftSseCallbacks {
  onProgress?: (data: SSEEventData) => void;
  onHeartbeat?: () => void;
}

export async function postDraftDocumentSse(
  requestData: Record<string, unknown>,
  accessToken: string | null | undefined,
  callbacks?: DraftSseCallbacks
): Promise<DraftStreamCompletion | null> {
  let currentEvent = "";

  const response = await fetch(apiUrl("/draft-document"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify(requestData),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("No response body");
  }

  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.length > 0 && !buffer.endsWith("\n") ? lines.pop() || "" : "";

    for (const line of lines) {
      const trimmedLine = line.trim();
      if (!trimmedLine) continue;

      if (trimmedLine.startsWith("event: ")) {
        currentEvent = trimmedLine.substring(7).trim();
      } else if (trimmedLine.startsWith("data: ")) {
        try {
          const dataStr = trimmedLine.substring(6);
          const data: SSEEventData = JSON.parse(dataStr);
          const eventType = currentEvent || "message";

          switch (eventType) {
            case "run-started":
              break;
            case "progress":
              callbacks?.onProgress?.(data);
              break;
            case "heartbeat":
              callbacks?.onHeartbeat?.();
              break;
            case "complete":
              return parseDraftCompletePayload(data);
            case "error": {
              const msg =
                typeof (data as { error?: string }).error === "string"
                  ? (data as { error: string }).error
                  : typeof data.message === "string"
                    ? data.message
                    : "Draft generation failed";
              throw new Error(msg);
            }
            case "message":
              if (data.status === "success") {
                const completed = parseDraftCompletePayload(data);
                if (completed) return completed;
              }
              break;
          }
          } catch (parseErr) {
            if (parseErr instanceof Error) throw parseErr;
            console.error("Failed to parse draft SSE data:", trimmedLine, parseErr);
          }
      }
    }
  }

  return null;
}
